import React from 'react';
import { Link } from 'react-router-dom';

interface GateReadiness {
  readiness_score: number;
  readiness_label: 'ready' | 'almost_ready' | 'making_progress' | 'more_practice';
  message: string;
  weak_area: string | null;
}

interface UnitProgressBarProps {
  unit: number;
  name?: string;
  avgMastery: number | null;
  lessonsTouched: number;
  totalLessons?: number;
  nextLesson?: number;
  unlocked?: boolean;
  passedGate?: boolean;
  gateReadiness?: GateReadiness;
}

const READINESS_COLORS: Record<string, string> = {
  ready: '#22c55e',
  almost_ready: '#eab308',
  making_progress: '#f97316',
  more_practice: '#ef4444',
};

export const UnitProgressBar: React.FC<UnitProgressBarProps> = ({
  unit,
  name,
  avgMastery,
  lessonsTouched,
  totalLessons = 10,
  nextLesson = 1,
  unlocked = true,
  passedGate = false,
  gateReadiness,
}) => {
  const mastery = avgMastery || 0;
  const masteryPercent = mastery * 100;
  const allLessonsComplete = nextLesson >= totalLessons;

  // Locked state
  if (!unlocked) {
    return (
      <div className="unit-progress unit-locked">
        <div className="unit-header">
          <h4>
            <span className="lock-icon">🔒</span>
            Unit {unit}{name ? `: ${name}` : ''}
          </h4>
        </div>
        <div className="locked-message">
          Complete Unit {unit - 1} Mastery Gate to unlock
        </div>
      </div>
    );
  }

  // Show gate readiness if not passed and have enough progress
  const showReadiness = !passedGate && gateReadiness && lessonsTouched >= 2;
  const readinessPercent = gateReadiness ? Math.round(gateReadiness.readiness_score * 100) : 0;

  return (
    <div className={`unit-progress ${passedGate ? 'unit-complete' : ''}`}>
      <div className="unit-header">
        <h4>
          {passedGate && <span className="complete-icon">✓</span>}
          Unit {unit}{name ? `: ${name}` : ''}
        </h4>
        <span className="mastery-percent" title="Accuracy rate">
          {masteryPercent.toFixed(0)}% accuracy
        </span>
      </div>

      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${masteryPercent}%` }}
        />
      </div>

      <div className="unit-footer">
        <span className="lessons-count">
          {lessonsTouched} / {totalLessons} lessons started
          {allLessonsComplete && !passedGate && ' ✓'}
        </span>
        <div className="unit-actions">
          {passedGate ? (
            <Link to={`/practice/${unit}`} className="continue-link">
              Review
            </Link>
          ) : allLessonsComplete ? (
            <>
              <Link to={`/mastery-gate/${unit}`} className="gate-link">
                Take Gate
              </Link>
              <Link to={`/practice/${unit}`} className="continue-link">
                Review
              </Link>
            </>
          ) : (
            <Link to={`/practice/${unit}/${nextLesson}`} className="continue-link">
              Continue Lesson {nextLesson}
            </Link>
          )}
        </div>
      </div>

      {/* Gate Readiness Indicator */}
      {showReadiness && (
        <div className="gate-readiness-indicator">
          <div className="readiness-bar-container">
            <div className="readiness-label">
              <span>Gate readiness:</span>
              <span
                className="readiness-percent"
                style={{ color: READINESS_COLORS[gateReadiness.readiness_label] }}
              >
                {readinessPercent}%
              </span>
            </div>
            <div className="readiness-bar">
              <div
                className="readiness-fill"
                style={{
                  width: `${readinessPercent}%`,
                  backgroundColor: READINESS_COLORS[gateReadiness.readiness_label],
                }}
              />
              <div className="readiness-threshold" style={{ left: '85%' }} title="Ready threshold (85%)" />
            </div>
          </div>
          {gateReadiness.weak_area && gateReadiness.readiness_label !== 'ready' && (
            <span className="readiness-hint">
              Weak area: {gateReadiness.weak_area}
            </span>
          )}
          {gateReadiness.readiness_label === 'ready' && (
            <Link to={`/mastery-gate/${unit}`} className="ready-badge">
              Ready for gate!
            </Link>
          )}
        </div>
      )}
    </div>
  );
};
